/**
 * Point Structure Models for Target Achievements
 *
 * Handles point structure templates with various types:
 * - Individual vs Cumulative slabs
 * - Flat, Fixed, Multiple point types
 * - Excel-based bulk configuration
 */

// ==================== ENUMS ====================

/**
 * Point Structure Type
 * 1 = Category (disabled in UI)
 * 2 = SKU
 */
export enum PointStructureType {
  CATEGORY = 1,
  SKU = 2,
}

/**
 * Slab Type
 * 1 = Individual
 * 2 = Cumulative
 */
export enum SlabType {
  INDIVIDUAL = 1,
  CUMULATIVE = 2,
}

/**
 * Point Type (for Individual slab)
 * 1 = Flat
 * 2 = Fixed
 * 3 = Multiple
 */
export enum PointType {
  FLAT = 1,
  FIXED = 2,
  MULTIPLE = 3,
}

/**
 * Cumulative Type
 * 1 = Collective
 * 2 = Singular
 */
export enum CumulativeType {
  COLLECTIVE = 1,
  SINGULAR = 2,
}

/**
 * Cumulative Sub Type
 * 1 = Flat
 * 2 = Fixed
 */
export enum CumulativeSubType {
  FLAT = 1,
  FIXED = 2,
}

// ==================== INTERFACES ====================

/**
 * Brand basic info for dropdown/multiselect
 */
export interface BrandBasicInfo {
  id: number;
  brand_name: string;
  brand_category_id?: number;
  sku?: string;
  status?: number;
}

/**
 * Flat cumulative row (dynamic form array)
 */
export interface FlatCumulativeRow {
  points: number;
  min_threshold: number;
  max_threshold: number;
}

/**
 * Point structure template list item
 */
export interface PointStructureTemplate {
  id: number;
  template_id?: number; // Alias for id - used in some API responses
  template_name: string;
  type_points_structure: string; // Display name
  type_of_slab: string; // Display name
  point_type: string | null; // Display name
  type_of_cummulative: string | null; // Display name
  sub_type_of_cumulative: string | null; // Display name
  calculation_type: string; // Display name (Percentage or Quantity)
  target_basis?: string; // Display name (No Target or Target Based)

  // IDs for reference
  type_of_pointstructure_id: number;
  type_of_slab_id: number;
  point_type_id: number | null;
  type_of_cumulative_id: number | null;
  typeof_cummulative_id?: number | null; // Alias for type_of_cumulative_id
  sub_type_of_cumulative_id: number | null;
  calculation_type_id: number; // 1 = Percentage Volume, 2 = Volume, 3 = Value / Percentage Value
  target_basis_id?: number; // 1 = No Target, 2 = Target Based

  // Template details for view modal
  template_details: PointStructureTemplateDetail[];

  // For cumulative fixed type
  min_threshold?: number;
  max_threshold?: number;
}

/**
 * Template detail row (for view modal)
 */
export interface PointStructureTemplateDetail {
  id: number;

  // Brand info
  brand_id?: number;
  brand_ids?: number[] | string; // Can be array or comma-separated string
  brand_name?: string;
  sku?: string;
  brand_details?: string; // Comma-separated brand names for popover

  // Threshold values
  min_threshold?: number;
  max_threshold?: number;

  // Slab values (for Individual slab types)
  points_start_slab?: number;
  points_end_slab?: number;

  // Points/multiplier
  points_value?: number;
  points_multiplier?: number;
  total_points?: number;
}

/**
 * Create point structure request payload
 */
export interface CreatePointStructureRequest {
  name: string;
  type_of_pointstructure: number;
  type_of_slab: number;
  point_type?: number;
  type_of_cumulative?: number;
  sub_type_of_cumulative?: number;
  flat_cumulative?: FlatCumulativeRow[];
  flat_brand_id?: number[];
  fixed_min_threshold?: number;
  fixed_max_threshold?: number;
  excel_file?: File;
}

/**
 * Create point structure response
 */
export interface CreatePointStructureResponse {
  success: boolean;
  status: number;
  data: {
    status: string;
    approved: number;
    rejected: number;
    reason: RejectedReason[];
  };
}

/**
 * Rejected reason item
 */
export interface RejectedReason {
  [key: string]: string | number;
}

/**
 * Pagination metadata returned alongside the point structure template list
 * (see loyalty-target/get-point-config-template `meta.pagination`)
 */
export interface PointStructureListPagination {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

/**
 * Get point structures response
 * `meta` is only present when the list endpoint is called with page/page_size
 * (e.g. not present when fetching a single template by id).
 */
export interface GetPointStructuresResponse {
  data: PointStructureTemplate[];
  meta?: {
    pagination: PointStructureListPagination;
  };
}

/**
 * Get brands response
 */
export interface GetBrandsResponse {
  data: BrandBasicInfo[];
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Get display name for point structure type
 */
export function getPointStructureTypeLabel(type: number): string {
  switch (type) {
    case PointStructureType.CATEGORY:
      return 'Category';
    case PointStructureType.SKU:
      return 'SKU';
    default:
      return 'Unknown';
  }
}

/**
 * Get display name for slab type
 */
export function getSlabTypeLabel(type: number): string {
  switch (type) {
    case SlabType.INDIVIDUAL:
      return 'Individual';
    case SlabType.CUMULATIVE:
      return 'Cumulative';
    default:
      return 'Unknown';
  }
}

/**
 * Last points column is "Percentage Value" only for a fixed configuration:
 * Individual + Point Type Fixed, or Cumulative (Collective/Singular) + Sub Type Fixed.
 * Every other configuration stays "Points Value".
 */
export function getPointsValueColumnHeader(template: {
  type_of_slab_id?: number | null;
  point_type_id?: number | null;
  type_of_cumulative_id?: number | null;
  typeof_cummulative_id?: number | null;
  sub_type_of_cumulative_id?: number | null;
} | null | undefined): string {
  if (!template) return 'Points Value';

  const slabType = Number(template.type_of_slab_id);
  const pointType = Number(template.point_type_id);
  const cumulativeType = Number(
    template.type_of_cumulative_id ?? template.typeof_cummulative_id
  );
  const cumulativeSubType = Number(template.sub_type_of_cumulative_id);

  const individualFixed =
    slabType === SlabType.INDIVIDUAL && pointType === PointType.FIXED;
  const cumulativeFixed =
    slabType === SlabType.CUMULATIVE &&
    (cumulativeType === CumulativeType.COLLECTIVE ||
      cumulativeType === CumulativeType.SINGULAR) &&
    cumulativeSubType === CumulativeSubType.FIXED;

  return individualFixed || cumulativeFixed ? 'Percentage Value' : 'Points Value';
}

/**
 * Get display name for point type
 */
export function getPointTypeLabel(type: number | null): string {
  if (type === null) return '-';
  switch (type) {
    case PointType.FLAT:
      return 'Flat';
    case PointType.FIXED:
      return 'Fixed';
    case PointType.MULTIPLE:
      return 'Multiple';
    default:
      return '-';
  }
}

/**
 * Get display name for cumulative type
 */
export function getCumulativeTypeLabel(type: number | null): string {
  if (type === null) return '-';
  switch (type) {
    case CumulativeType.COLLECTIVE:
      return 'Collective';
    case CumulativeType.SINGULAR:
      return 'Singular';
    default:
      return '-';
  }
}

/**
 * Get display name for cumulative sub type
 */
export function getCumulativeSubTypeLabel(type: number | null): string {
  if (type === null) return '-';
  switch (type) {
    case CumulativeSubType.FLAT:
      return 'Flat';
    case CumulativeSubType.FIXED:
      return 'Fixed';
    default:
      return '-';
  }
}

/**
 * Get badge color class for slab type
 */
export function getSlabTypeBadgeClass(type: number): string {
  switch (type) {
    case SlabType.INDIVIDUAL:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case SlabType.CUMULATIVE:
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

/**
 * Get badge color class for point type
 */
export function getPointTypeBadgeClass(type: number | null): string {
  if (type === null) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  switch (type) {
    case PointType.FLAT:
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case PointType.FIXED:
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
    case PointType.MULTIPLE:
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

/**
 * Get badge color class for calculation type
 */
export function getCalculationTypeBadgeClass(type: number): string {
  switch (type) {
    case 1: // Percentage
      return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300';
    case 2: // Quantity
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

/**
 * Get badge color class for target basis
 */
export function getTargetBasisBadgeClass(type: number | undefined): string {
  if (type === undefined) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  switch (type) {
    case 1: // No Target
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300';
    case 2: // Target Based
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

/**
 * Get badge color class for point structure type
 */
export function getPointStructureTypeBadgeClass(type: number): string {
  switch (type) {
    case PointStructureType.CATEGORY:
      return 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300';
    case PointStructureType.SKU:
      return 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}
